/**
 * Navigation store — thin wrapper around react-router for imperative navigation
 * from Zustand stores (which can't use hooks), plus the mobile sidebar drawer's
 * open/closed state.
 *
 * Components should use react-router's useNavigate / <Link> directly.
 * This store exists so chatStore.openConversation() can navigate, and so the
 * Header hamburger, the Sidebar drawer, and the AppShell backdrop can all
 * coordinate the off-canvas drawer without threading props through the shell.
 */

import { create } from "zustand";
import type { NavigateFunction } from "react-router-dom";

interface NavState {
  /** Set by AppShell on mount via useNavigate() */
  _navigate: NavigateFunction | null;
  register: (fn: NavigateFunction) => void;
  navigate: (path: string) => void;
  /**
   * Off-canvas sidebar drawer state — only meaningful below the `md`
   * breakpoint, where the sidebar is a slide-in overlay rather than a fixed
   * rail. Desktop ignores it (the rail is always visible via CSS). Opening is
   * driven by the Header hamburger; closing by the backdrop, a route change,
   * or selecting a conversation.
   */
  mobileNavOpen: boolean;
  openMobileNav: () => void;
  closeMobileNav: () => void;
  toggleMobileNav: () => void;
}

export const useNavStore = create<NavState>((set, get) => ({
  _navigate: null,
  register: (fn) => set({ _navigate: fn }),
  navigate: (path) => {
    const nav = get()._navigate;
    if (nav) nav(path);
  },
  mobileNavOpen: false,
  openMobileNav: () => set({ mobileNavOpen: true }),
  closeMobileNav: () => set({ mobileNavOpen: false }),
  toggleMobileNav: () => set((s) => ({ mobileNavOpen: !s.mobileNavOpen })),
}));
